import React, { useState, useEffect, useRef, useCallback } from 'react';
import { stockIssueAPI, storeAPI, itemAPI, unitAPI, stockAPI } from '../lib/api';
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from '../components/ui/card';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { 
  ArrowUpFromLine, 
  Search, 
  Loader2, 
  Check, 
  ChevronRight,
  Star,
  Clock,
  Package,
  Store,
  Hash,
  AlertCircle,
  RotateCcw,
  Zap
} from 'lucide-react';
import { toast } from 'sonner';
import { formatNumber, ISSUE_TO_OPTIONS } from '../lib/utils';
import { useAuth } from '../context/AuthContext';
import { cn } from '../lib/utils';

// Quick Entry Steps
const STEPS = {
  SELECT_STORE: 1,
  SEARCH_ITEM: 2,
  ENTER_QUANTITY: 3,
  CONFIRM: 4
};

export const QuickEntryPage = () => {
  const { user } = useAuth();
  const [currentStep, setCurrentStep] = useState(STEPS.SELECT_STORE);
  const [stores, setStores] = useState([]);
  const [items, setItems] = useState([]);
  const [units, setUnits] = useState([]);
  const [stock, setStock] = useState([]);
  const [loading, setLoading] = useState(true);
  const [submitting, setSubmitting] = useState(false);
  
  // Form state
  const [selectedStore, setSelectedStore] = useState(null);
  const [selectedItem, setSelectedItem] = useState(null);
  const [quantity, setQuantity] = useState('');
  const [issueTo, setIssueTo] = useState('Kitchen');
  
  // Search state
  const [searchQuery, setSearchQuery] = useState('');
  const [searchResults, setSearchResults] = useState([]);
  const searchInputRef = useRef(null);
  const quantityInputRef = useRef(null);
  
  // Recent & Favorites (stored in localStorage)
  const [recentItems, setRecentItems] = useState([]);
  const [favorites, setFavorites] = useState([]);
  
  // Smart defaults from localStorage
  const [lastUsedStore, setLastUsedStore] = useState(null);

  // Load data on mount
  useEffect(() => {
    fetchData();
    loadLocalPreferences();
  }, []);

  const fetchData = async () => {
    try {
      setLoading(true);
      const [storesRes, itemsRes, unitsRes, stockRes] = await Promise.all([
        storeAPI.getAll({ status: 'active' }),
        itemAPI.getAll({ status: 'active' }),
        unitAPI.getAll({ status: 'active' }),
        stockAPI.getAll()
      ]);
      setStores(storesRes.data);
      setItems(itemsRes.data);
      setUnits(unitsRes.data);
      setStock(stockRes.data);
    } catch (error) {
      toast.error('Failed to load data');
    } finally {
      setLoading(false);
    }
  };

  const loadLocalPreferences = () => {
    try {
      const savedRecent = localStorage.getItem('quickEntry_recentItems');
      const savedFavorites = localStorage.getItem('quickEntry_favorites');
      const savedLastStore = localStorage.getItem('quickEntry_lastStore');
      
      if (savedRecent) setRecentItems(JSON.parse(savedRecent));
      if (savedFavorites) setFavorites(JSON.parse(savedFavorites));
      if (savedLastStore) setLastUsedStore(JSON.parse(savedLastStore));
    } catch (e) {
      console.error('Error loading preferences:', e);
    }
  };

  const saveRecentItem = (item) => {
    const updated = [item, ...recentItems.filter(i => i.id !== item.id)].slice(0, 10);
    setRecentItems(updated);
    localStorage.setItem('quickEntry_recentItems', JSON.stringify(updated));
  };

  const saveLastStore = (store) => {
    setLastUsedStore(store);
    localStorage.setItem('quickEntry_lastStore', JSON.stringify(store));
  };

  const toggleFavorite = (item) => {
    const isFav = favorites.some(f => f.id === item.id);
    let updated;
    if (isFav) {
      updated = favorites.filter(f => f.id !== item.id);
    } else {
      updated = [...favorites, item];
    }
    setFavorites(updated);
    localStorage.setItem('quickEntry_favorites', JSON.stringify(updated));
    toast.success(isFav ? 'Removed from favorites' : 'Added to favorites');
  };

  // Smart search with instant results
  const handleSearch = useCallback((query) => {
    setSearchQuery(query);
    if (!query.trim()) {
      setSearchResults([]);
      return;
    }
    
    const lowerQuery = query.toLowerCase();
    const results = items.filter(item => 
      item.name.toLowerCase().includes(lowerQuery) ||
      item.code?.toLowerCase().includes(lowerQuery) ||
      item.category_name?.toLowerCase().includes(lowerQuery)
    ).slice(0, 10);
    
    setSearchResults(results);
  }, [items]);

  // Get available stock for selected item in selected store
  const getAvailableStock = (itemId, storeId) => {
    const stockRecord = stock.find(s => s.item_id === itemId && s.store_id === storeId);
    return stockRecord?.quantity || 0;
  };

  // Handle store selection
  const handleStoreSelect = (store) => {
    setSelectedStore(store);
    saveLastStore(store);
    setCurrentStep(STEPS.SEARCH_ITEM);
    setTimeout(() => searchInputRef.current?.focus(), 100);
  };

  // Handle item selection
  const handleItemSelect = (item) => {
    setSelectedItem(item);
    saveRecentItem(item);
    setSearchQuery('');
    setSearchResults([]);
    setCurrentStep(STEPS.ENTER_QUANTITY);
    setTimeout(() => quantityInputRef.current?.focus(), 100);
  };

  // Handle quantity submit
  const handleQuantitySubmit = () => {
    const qty = parseFloat(quantity);
    const available = getAvailableStock(selectedItem.id, selectedStore.id);
    
    if (!qty || qty <= 0) {
      toast.error('Please enter a valid quantity');
      return;
    }
    
    if (qty > available) {
      toast.error(`Only ${formatNumber(available)} ${selectedItem.unit_name || 'units'} available`);
      return;
    }
    
    setCurrentStep(STEPS.CONFIRM);
  };

  // Final submission
  const handleSubmit = async () => {
    try {
      setSubmitting(true);
      
      const payload = {
        property_id: selectedStore.property_id,
        from_store_id: selectedStore.id,
        issue_to: issueTo,
        items: [{
          item_id: selectedItem.id,
          quantity: parseFloat(quantity),
          unit_id: selectedItem.default_unit_id
        }]
      };
      
      await stockIssueAPI.create(payload);
      
      // Show success with summary
      toast.success(
        <div className="space-y-1">
          <p className="font-semibold">Stock Issue Successful!</p>
          <p className="text-sm">Item: {selectedItem.name}</p>
          <p className="text-sm">Quantity: {quantity} {selectedItem.unit_name || ''}</p>
          <p className="text-sm">From: {selectedStore.name}</p>
        </div>,
        { duration: 4000 }
      );
      
      // Reset for next entry
      handleReset();
      
    } catch (error) {
      const message = error.response?.data?.detail || 'Failed to issue stock';
      toast.error(message);
    } finally {
      setSubmitting(false);
    }
  };

  // Reset to start
  const handleReset = () => {
    setCurrentStep(STEPS.SELECT_STORE);
    setSelectedItem(null);
    setQuantity('');
    setSearchQuery('');
    setSearchResults([]);
    // Keep selected store for faster repeat entries
  };

  // Go back one step
  const handleBack = () => {
    if (currentStep === STEPS.SEARCH_ITEM) {
      setCurrentStep(STEPS.SELECT_STORE);
    } else if (currentStep === STEPS.ENTER_QUANTITY) {
      setCurrentStep(STEPS.SEARCH_ITEM);
      setSelectedItem(null);
    } else if (currentStep === STEPS.CONFIRM) {
      setCurrentStep(STEPS.ENTER_QUANTITY);
    }
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center h-64">
        <Loader2 className="h-8 w-8 animate-spin text-muted-foreground" />
      </div>
    );
  }

  const availableStock = selectedItem && selectedStore 
    ? getAvailableStock(selectedItem.id, selectedStore.id) 
    : 0;

  return (
    <div className="max-w-2xl mx-auto space-y-6 animate-fade-in">
      {/* Header */}
      <div className="text-center space-y-2">
        <div className="inline-flex items-center gap-2 px-4 py-2 bg-primary/10 rounded-full">
          <Zap className="h-5 w-5 text-primary" />
          <span className="font-semibold text-primary">Quick Stock Issue</span>
        </div>
        <p className="text-muted-foreground">Issue items in seconds</p>
      </div>

      {/* Progress Steps */}
      <div className="flex items-center justify-center gap-2">
        {[1, 2, 3, 4].map((step) => (
          <React.Fragment key={step}>
            <div 
              className={cn(
                "w-10 h-10 rounded-full flex items-center justify-center font-semibold transition-all",
                currentStep >= step 
                  ? "bg-primary text-primary-foreground" 
                  : "bg-muted text-muted-foreground"
              )}
            >
              {currentStep > step ? <Check className="h-5 w-5" /> : step}
            </div>
            {step < 4 && (
              <div className={cn(
                "w-12 h-1 rounded-full transition-all",
                currentStep > step ? "bg-primary" : "bg-muted"
              )} />
            )}
          </React.Fragment>
        ))}
      </div>

      {/* Step Labels */}
      <div className="flex justify-between text-xs text-muted-foreground px-2">
        <span className={currentStep >= 1 ? "text-primary font-medium" : ""}>Store</span>
        <span className={currentStep >= 2 ? "text-primary font-medium" : ""}>Item</span>
        <span className={currentStep >= 3 ? "text-primary font-medium" : ""}>Quantity</span>
        <span className={currentStep >= 4 ? "text-primary font-medium" : ""}>Confirm</span>
      </div>

      {/* Step 1: Select Store */}
      {currentStep === STEPS.SELECT_STORE && (
        <Card data-testid="quick-entry-store-step">
          <CardHeader className="pb-4">
            <CardTitle className="flex items-center gap-2">
              <Store className="h-5 w-5" />
              Select Store
            </CardTitle>
            <CardDescription>Where are you issuing from?</CardDescription>
          </CardHeader>
          <CardContent className="space-y-4">
            {/* Smart Default - Last Used Store */}
            {lastUsedStore && (
              <div className="space-y-2">
                <p className="text-xs text-muted-foreground font-medium uppercase tracking-wide">Last Used</p>
                <Button
                  variant="outline"
                  className="w-full h-14 justify-start gap-3 border-primary/50 bg-primary/5"
                  onClick={() => handleStoreSelect(lastUsedStore)}
                  data-testid="last-used-store"
                >
                  <Clock className="h-5 w-5 text-primary" />
                  <div className="text-left">
                    <p className="font-medium">{lastUsedStore.name}</p>
                    <p className="text-xs text-muted-foreground">{lastUsedStore.store_type || 'Store'}</p>
                  </div>
                </Button>
              </div>
            )}
            
            {/* All Stores */}
            <div className="space-y-2">
              <p className="text-xs text-muted-foreground font-medium uppercase tracking-wide">All Stores</p>
              <div className="grid gap-2">
                {stores.map((store) => (
                  <Button
                    key={store.id}
                    variant="outline"
                    className="w-full h-12 justify-start gap-3"
                    onClick={() => handleStoreSelect(store)}
                    data-testid={`store-${store.id}`}
                  >
                    <Store className="h-4 w-4" />
                    <div className="text-left">
                      <p className="font-medium">{store.name}</p>
                    </div>
                    <ChevronRight className="h-4 w-4 ml-auto text-muted-foreground" />
                  </Button>
                ))}
              </div>
            </div>
          </CardContent>
        </Card>
      )}

      {/* Step 2: Search Item */}
      {currentStep === STEPS.SEARCH_ITEM && (
        <Card data-testid="quick-entry-item-step">
          <CardHeader className="pb-4">
            <CardTitle className="flex items-center gap-2">
              <Package className="h-5 w-5" />
              Search Item
            </CardTitle>
            <CardDescription>
              Issuing from <span className="font-medium text-foreground">{selectedStore?.name}</span>
            </CardDescription>
          </CardHeader>
          <CardContent className="space-y-4">
            {/* Search Input */}
            <div className="relative">
              <Search className="absolute left-3 top-1/2 -translate-y-1/2 h-5 w-5 text-muted-foreground" />
              <Input
                ref={searchInputRef}
                type="text"
                placeholder="Type item name, code, or category..."
                value={searchQuery}
                onChange={(e) => handleSearch(e.target.value)}
                className="pl-10 h-12 text-lg"
                autoFocus
                data-testid="item-search-input"
              />
            </div>

            {/* Search Results */}
            {searchResults.length > 0 && (
              <div className="space-y-2 max-h-64 overflow-y-auto">
                {searchResults.map((item) => {
                  const itemStock = getAvailableStock(item.id, selectedStore.id);
                  const isFav = favorites.some(f => f.id === item.id);
                  return (
                    <div
                      key={item.id}
                      className="flex items-center gap-3 p-3 rounded-lg border hover:bg-muted/50 cursor-pointer transition-colors"
                      onClick={() => handleItemSelect(item)}
                      data-testid={`search-result-${item.id}`}
                    >
                      <Package className="h-5 w-5 text-muted-foreground" />
                      <div className="flex-1 min-w-0">
                        <p className="font-medium truncate">{item.name}</p>
                        <p className="text-xs text-muted-foreground">
                          {item.category_name || 'No category'} • {item.unit_name || 'Unit'}
                        </p>
                      </div>
                      <div className="text-right">
                        <p className={cn(
                          "font-semibold",
                          itemStock <= 0 ? "text-red-500" : "text-green-600"
                        )}>
                          {formatNumber(itemStock)}
                        </p>
                        <p className="text-xs text-muted-foreground">available</p>
                      </div>
                      <Button
                        variant="ghost"
                        size="icon"
                        className="h-8 w-8"
                        onClick={(e) => { e.stopPropagation(); toggleFavorite(item); }}
                      >
                        <Star className={cn("h-4 w-4", isFav && "fill-yellow-400 text-yellow-400")} />
                      </Button>
                    </div>
                  );
                })}
              </div>
            )}

            {/* No search - show favorites and recent */}
            {!searchQuery && (
              <>
                {/* Favorites */}
                {favorites.length > 0 && (
                  <div className="space-y-2">
                    <p className="text-xs text-muted-foreground font-medium uppercase tracking-wide flex items-center gap-1">
                      <Star className="h-3 w-3" /> Favorites
                    </p>
                    <div className="grid gap-2">
                      {favorites.slice(0, 5).map((item) => {
                        const itemStock = getAvailableStock(item.id, selectedStore.id);
                        return (
                          <Button
                            key={item.id}
                            variant="outline"
                            className="w-full h-12 justify-start gap-3"
                            onClick={() => handleItemSelect(item)}
                            data-testid={`favorite-${item.id}`}
                          >
                            <Star className="h-4 w-4 fill-yellow-400 text-yellow-400" />
                            <span className="flex-1 text-left truncate">{item.name}</span>
                            <span className={cn(
                              "text-sm font-medium",
                              itemStock <= 0 ? "text-red-500" : "text-green-600"
                            )}>
                              {formatNumber(itemStock)}
                            </span>
                          </Button>
                        );
                      })}
                    </div>
                  </div>
                )}

                {/* Recent Items */}
                {recentItems.length > 0 && (
                  <div className="space-y-2">
                    <p className="text-xs text-muted-foreground font-medium uppercase tracking-wide flex items-center gap-1">
                      <Clock className="h-3 w-3" /> Recent
                    </p>
                    <div className="grid gap-2">
                      {recentItems.slice(0, 5).map((item) => {
                        const itemStock = getAvailableStock(item.id, selectedStore.id);
                        return (
                          <Button
                            key={item.id}
                            variant="outline"
                            className="w-full h-12 justify-start gap-3"
                            onClick={() => handleItemSelect(item)}
                            data-testid={`recent-${item.id}`}
                          >
                            <Clock className="h-4 w-4 text-muted-foreground" />
                            <span className="flex-1 text-left truncate">{item.name}</span>
                            <span className={cn(
                              "text-sm font-medium",
                              itemStock <= 0 ? "text-red-500" : "text-green-600"
                            )}>
                              {formatNumber(itemStock)}
                            </span>
                          </Button>
                        );
                      })}
                    </div>
                  </div>
                )}

                {/* Empty state */}
                {favorites.length === 0 && recentItems.length === 0 && (
                  <div className="text-center py-8 text-muted-foreground">
                    <Search className="h-10 w-10 mx-auto mb-2 opacity-30" />
                    <p>Start typing to search items</p>
                  </div>
                )}
              </>
            )}

            {/* Back button */}
            <Button variant="ghost" onClick={handleBack} className="w-full">
              ← Back to Store Selection
            </Button>
          </CardContent>
        </Card>
      )}

      {/* Step 3: Enter Quantity */}
      {currentStep === STEPS.ENTER_QUANTITY && (
        <Card data-testid="quick-entry-quantity-step">
          <CardHeader className="pb-4">
            <CardTitle className="flex items-center gap-2">
              <Hash className="h-5 w-5" />
              Enter Quantity
            </CardTitle>
            <CardDescription>
              How many <span className="font-medium text-foreground">{selectedItem?.name}</span>?
            </CardDescription>
          </CardHeader>
          <CardContent className="space-y-4">
            {/* Item Summary */}
            <div className="p-4 rounded-lg bg-muted/50 space-y-2">
              <div className="flex justify-between">
                <span className="text-muted-foreground">Item</span>
                <span className="font-medium">{selectedItem?.name}</span>
              </div>
              <div className="flex justify-between">
                <span className="text-muted-foreground">Store</span>
                <span className="font-medium">{selectedStore?.name}</span>
              </div>
              <div className="flex justify-between">
                <span className="text-muted-foreground">Available</span>
                <span className={cn(
                  "font-semibold",
                  availableStock <= 0 ? "text-red-500" : "text-green-600"
                )}>
                  {formatNumber(availableStock)} {selectedItem?.unit_name || ''}
                </span>
              </div>
            </div>

            {/* Quantity Input */}
            <div className="space-y-2">
              <div className="relative">
                <Input
                  ref={quantityInputRef}
                  type="number"
                  placeholder="Enter quantity"
                  value={quantity}
                  onChange={(e) => setQuantity(e.target.value)}
                  className="h-16 text-2xl text-center pr-20"
                  min="0"
                  step="0.01"
                  autoFocus
                  data-testid="quantity-input"
                />
                <span className="absolute right-4 top-1/2 -translate-y-1/2 text-muted-foreground">
                  {selectedItem?.unit_name || 'units'}
                </span>
              </div>
              
              {/* Quick quantity buttons */}
              <div className="flex gap-2 justify-center">
                {[1, 5, 10, 25].map((num) => (
                  <Button
                    key={num}
                    variant="outline"
                    size="sm"
                    onClick={() => setQuantity(num.toString())}
                    disabled={num > availableStock}
                    data-testid={`quick-qty-${num}`}
                  >
                    {num}
                  </Button>
                ))}
              </div>
            </div>

            {/* Issue To Selection */}
            <div className="space-y-2">
              <p className="text-sm text-muted-foreground">Issue To</p>
              <div className="flex flex-wrap gap-2">
                {ISSUE_TO_OPTIONS.map((option) => (
                  <Button
                    key={option}
                    variant={issueTo === option ? "default" : "outline"}
                    size="sm"
                    onClick={() => setIssueTo(option)}
                    data-testid={`issue-to-${option.toLowerCase()}`}
                  >
                    {option}
                  </Button>
                ))}
              </div>
            </div>

            {/* Warning for high quantity */}
            {parseFloat(quantity) > availableStock * 0.5 && parseFloat(quantity) <= availableStock && (
              <div className="flex items-center gap-2 p-3 rounded-lg bg-amber-50 border border-amber-200 text-amber-700">
                <AlertCircle className="h-4 w-4" />
                <span className="text-sm">This is more than 50% of available stock</span>
              </div>
            )}

            {/* Error for exceeding stock */}
            {parseFloat(quantity) > availableStock && (
              <div className="flex items-center gap-2 p-3 rounded-lg bg-red-50 border border-red-200 text-red-700">
                <AlertCircle className="h-4 w-4" />
                <span className="text-sm">Cannot issue more than available ({formatNumber(availableStock)})</span>
              </div>
            )}

            {/* Action Buttons */}
            <div className="flex gap-3">
              <Button variant="outline" onClick={handleBack} className="flex-1">
                ← Back
              </Button>
              <Button 
                onClick={handleQuantitySubmit} 
                className="flex-1"
                disabled={!quantity || parseFloat(quantity) <= 0 || parseFloat(quantity) > availableStock}
                data-testid="continue-to-confirm"
              >
                Continue →
              </Button>
            </div>
          </CardContent>
        </Card>
      )}

      {/* Step 4: Confirm */}
      {currentStep === STEPS.CONFIRM && (
        <Card data-testid="quick-entry-confirm-step">
          <CardHeader className="pb-4">
            <CardTitle className="flex items-center gap-2">
              <Check className="h-5 w-5" />
              Confirm Issue
            </CardTitle>
            <CardDescription>Review and confirm your stock issue</CardDescription>
          </CardHeader>
          <CardContent className="space-y-4">
            {/* Summary */}
            <div className="p-6 rounded-xl bg-gradient-to-br from-primary/10 to-primary/5 border border-primary/20 space-y-4">
              <div className="flex justify-between items-center">
                <span className="text-muted-foreground">Item</span>
                <span className="font-semibold text-lg">{selectedItem?.name}</span>
              </div>
              <div className="flex justify-between items-center">
                <span className="text-muted-foreground">Quantity</span>
                <span className="font-bold text-2xl text-primary">
                  {quantity} {selectedItem?.unit_name || ''}
                </span>
              </div>
              <div className="flex justify-between items-center">
                <span className="text-muted-foreground">From Store</span>
                <span className="font-medium">{selectedStore?.name}</span>
              </div>
              <div className="flex justify-between items-center">
                <span className="text-muted-foreground">Issue To</span>
                <span className="font-medium">{issueTo}</span>
              </div>
            </div>

            {/* Stock After Issue */}
            <div className="p-4 rounded-lg bg-muted/50">
              <div className="flex justify-between">
                <span className="text-muted-foreground">Stock after issue</span>
                <span className="font-medium">
                  {formatNumber(availableStock - parseFloat(quantity))} {selectedItem?.unit_name || ''}
                </span>
              </div>
            </div>

            {/* Action Buttons */}
            <div className="flex gap-3">
              <Button variant="outline" onClick={handleBack} className="flex-1">
                ← Edit
              </Button>
              <Button 
                onClick={handleSubmit} 
                className="flex-1 h-12 text-lg"
                disabled={submitting}
                data-testid="confirm-issue-btn"
              >
                {submitting ? (
                  <Loader2 className="h-5 w-5 animate-spin mr-2" />
                ) : (
                  <ArrowUpFromLine className="h-5 w-5 mr-2" />
                )}
                Issue Stock
              </Button>
            </div>
          </CardContent>
        </Card>
      )}

      {/* Quick Actions Footer */}
      <div className="flex justify-center gap-4 text-sm">
        <Button variant="ghost" size="sm" onClick={handleReset} data-testid="start-over-btn">
          <RotateCcw className="h-4 w-4 mr-1" />
          Start Over
        </Button>
      </div>
    </div>
  );
};

export default QuickEntryPage;
